import React, { memo, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { Image } from '@sg/garnish';

import { HexagonStroke, HexagonStrokeActive } from '../../assets';

// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsPromptHexagonStroke = memo((props: Props) => {
  const { scale = 1, delay = 0 } = props;

  // ─── Animations ──────────────────────────────────────────────────────

  const opacitySV = useSharedValue(0);
  const overlayOpacitySV = useSharedValue(0);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerAnimatedStyles = useAnimatedStyle(() => ({
    opacity: opacitySV.value,
  }));

  const overlayAnimatedStyles = useAnimatedStyle(() => ({
    opacity: overlayOpacitySV.value,
  }));

  const containerDynamicStyles = useStyle(() => ({ transform: [{ scale }] }), [scale]);

  const containerStyles = [styles.container, containerDynamicStyles, containerAnimatedStyles];
  const overlayStyles = [styles.overlay, overlayAnimatedStyles];

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    opacitySV.value = withDelay(delay, withTiming(1, { duration: FADE_IN_DURATION }));

    overlayOpacitySV.value = withDelay(
      delay + BREATH_INTRO_DELAY,
      withRepeat(
        withSequence(
          withTiming(1, { duration: BREATH_DURATION }),
          withTiming(0, { duration: BREATH_DURATION }),
        ),
        -1,
      ),
    );
  }, [delay, opacitySV, overlayOpacitySV]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      style={containerStyles}
      accessible={false}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <Image source={HexagonStroke} style={styles.image} contentFit="contain" />

      <Animated.View style={overlayStyles}>
        <Image source={HexagonStrokeActive} style={styles.image} contentFit="contain" />
      </Animated.View>
    </Animated.View>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const FADE_IN_DURATION = 500;
const BREATH_INTRO_DELAY = 1000;
const BREATH_DURATION = 1500;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
  },
  overlay: {
    position: 'absolute',
  },
  image: {
    width: 500,
    height: 500,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  scale?: number;
  delay?: number;
};
